/**
 * Prashasta Public Analytics Tracker
 *
 * First-party, privacy-conscious analytics for the public portal.
 * - Anonymous visitor ID via localStorage (first-party UUID, not a fingerprint)
 * - Session ID via sessionStorage (reset on browser close)
 * - Fire-and-forget event recording (never blocks page load)
 * - No precise GPS, no cross-site tracking, no device fingerprinting
 * - All events go to /api/public-analytics/* endpoints
 * - Fails silently — tracker errors never affect the portal experience
 */

const VISITOR_KEY  = 'prashasta_vid';
const SESSION_KEY  = 'prashasta_sid';
const SESSION_DATA = 'prashasta_sdata';
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ── UUID generator (simple, no crypto required) ───────────────────────────────
function genUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

// ── Visitor ID — persists across sessions ─────────────────────────────────────
function getVisitorId() {
  try {
    let vid = localStorage.getItem(VISITOR_KEY);
    if (!vid) {
      vid = genUUID();
      localStorage.setItem(VISITOR_KEY, vid);
    }
    return vid;
  } catch (_) {
    return genUUID(); // localStorage blocked (private browsing with strict settings)
  }
}

// ── Session ID — resets on browser close ──────────────────────────────────────
function getSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = genUUID();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch (_) {
    return genUUID();
  }
}

// ── UTM / attribution parse ───────────────────────────────────────────────────
function parseAttribution() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      source:   p.get('utm_source')   || null,
      medium:   p.get('utm_medium')   || null,
      campaign: p.get('utm_campaign') || null,
      content:  p.get('utm_content')  || null,
      term:     p.get('utm_term')     || null,
      click_id: p.get('gclid') || p.get('fbclid') || null,
    };
  } catch (_) {
    return {};
  }
}

function getReferrerDomain() {
  try {
    const ref = document.referrer;
    if (!ref) return null;
    return new URL(ref).hostname;
  } catch (_) {
    return null;
  }
}

function getDeviceType() {
  try {
    const w = window.innerWidth;
    if (w >= 1024) return 'DESKTOP';
    if (w >= 768)  return 'TABLET';
    return 'MOBILE';
  } catch (_) {
    return 'UNKNOWN';
  }
}

// ── Fire-and-forget POST ──────────────────────────────────────────────────────
function fire(path, body) {
  try {
    const url = `${API_BASE}/api${path}`;
    const json = JSON.stringify(body);
    if (navigator.sendBeacon) {
      // sendBeacon defaults to text/plain — wrap in Blob so FastAPI parses JSON correctly
      const blob = new Blob([json], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: json,
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_) {
    // Never throw — analytics must not break the portal
  }
}

// ── Session tracking state ────────────────────────────────────────────────────
let _sessionStarted = false;

function ensureSessionStarted() {
  if (_sessionStarted) return;
  _sessionStarted = true;

  const vid = getVisitorId();
  const sid = getSessionId();
  const attr = parseAttribution();
  const landingPage = window.location.pathname + window.location.search;

  // Store session data for reuse
  try {
    sessionStorage.setItem(SESSION_DATA, JSON.stringify({ vid, sid, attr, landingPage }));
  } catch (_) {}

  fire('/public-analytics/session/start', {
    visitor_id:      vid,
    session_id:      sid,
    landing_page:    landingPage,
    source:          attr.source,
    medium:          attr.medium,
    campaign:        attr.campaign,
    content:         attr.content,
    term:            attr.term,
    click_id:        attr.click_id,
    referrer_domain: getReferrerDomain(),
    device_type:     getDeviceType(),
  });
}

function getSessionData() {
  try {
    const raw = sessionStorage.getItem(SESSION_DATA);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  // Fallback — reconstruct
  const vid = getVisitorId();
  const sid = getSessionId();
  const attr = parseAttribution();
  const landingPage = window.location.pathname;
  return { vid, sid, attr, landingPage };
}


// ═══════════════════════════════════════════════════════════════════════════════
// PUBLIC API
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Track a public portal page view.
 * Call on every page load in the public portal.
 */
export function trackPageView(pagePath) {
  ensureSessionStarted();
  const { vid, sid } = getSessionData();
  fire('/public-analytics/event', {
    visitor_id: vid,
    session_id: sid,
    event_type: 'PAGE_VIEWED',
    page_path:  pagePath || window.location.pathname,
  });
}

/**
 * Track any public portal behavior event.
 * @param {string} eventType - one of the canonical ALLOWED_EVENT_TYPES
 * @param {object} extras - optional: { event_type_category, service_category_id, metadata, page_path }
 */
export function trackEvent(eventType, extras = {}) {
  ensureSessionStarted();
  const { vid, sid } = getSessionData();
  fire('/public-analytics/event', {
    visitor_id:          vid,
    session_id:          sid,
    event_type:          eventType,
    page_path:           extras.page_path || window.location.pathname,
    event_type_category: extras.event_type_category || null,
    service_category_id: extras.service_category_id || null,
    design_element_type: extras.design_element_type || null,
    event_interest_type: extras.event_interest_type || null,
    price_band_viewed:   extras.price_band_viewed || null,
    metadata:            extras.metadata || null,
  });
}

/**
 * Record user-declared geo at high-intent steps (Date Check / BME city/postal entry).
 * @param {object} geo - { city, region, country, postal_code }
 */
export function trackGeo(geo = {}) {
  ensureSessionStarted();
  const { vid, sid } = getSessionData();
  fire('/public-analytics/geo', {
    visitor_id:  vid,
    session_id:  sid,
    city:        geo.city || null,
    region:      geo.region || null,
    country:     geo.country || 'IN',  // Default India — user can override
    postal_code: geo.postal_code || geo.pin || null,
    geo_source:  'USER_DECLARED',
  });
}

/**
 * Record a pre-lead (inquiry submitted / BME result reached).
 * @param {object} data - { prelead_reference (journey_session_id), event_type, event_city, event_postal_code, ... }
 */
export function trackPreLead(data = {}) {
  ensureSessionStarted();
  const { vid, sid } = getSessionData();
  fire('/public-analytics/prelead', {
    visitor_id:        vid,
    session_id:        sid,
    prelead_reference: data.prelead_reference || getSessionId(),
    event_type:        data.event_type || null,
    event_city:        data.event_city || null,
    event_area:        data.event_area || null,
    event_postal_code: data.event_postal_code || null,
    preferred_date:    data.preferred_date || null,
    guest_count:       data.guest_count || null,
    intent_source:     data.intent_source || 'PORTAL',
  });
}

/**
 * Record the terminal LEAD_CREATED event.
 * Only stores lead_reference_id — never queries back to CRM.
 * @param {object} data - { lead_reference_id, prelead_reference, conversion_source }
 */
export function trackLeadCreated(data = {}) {
  ensureSessionStarted();
  const { vid, sid } = getSessionData();
  fire('/public-analytics/lead-conversion', {
    visitor_id:        vid,
    session_id:        sid,
    prelead_reference: data.prelead_reference || null,
    lead_reference_id: data.lead_reference_id,
    conversion_source: data.conversion_source || 'BUILD_MY_EVENT',
  });
}

/**
 * Get the analytics visitor and session IDs for passing to the CRM provisional lead API.
 * Used to link the BME journey to the analytics funnel.
 */
export function getAnalyticsIds() {
  ensureSessionStarted();
  const { vid, sid } = getSessionData();
  return { visitor_id: vid, analytics_session_id: sid };
}

/**
 * Signal session end (called on beforeunload).
 * Uses sendBeacon for reliability.
 */
export function endSession(exitPage) {
  try {
    const { vid, sid } = getSessionData();
    const url = `${API_BASE}/api/public-analytics/session/end`;
    const body = JSON.stringify({ visitor_id: vid, session_id: sid, exit_page: exitPage });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, body);
    }
  } catch (_) {}
}

// Auto-start session when this module is imported in a browser context
if (typeof window !== 'undefined') {
  // Defer to not block rendering
  setTimeout(ensureSessionStarted, 0);
  window.addEventListener('beforeunload', () => endSession(window.location.pathname));
}
